import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { PerformanceAnalysisJobFilter } from '../../enums/performance-analysis-job-filter.enum';
import { PerformanceAnalysisPeriod } from '../../enums/performance-analysis-period.enum';
import {
  PerformanceAnalysisData,
  PerformanceAnalysisFilters,
} from '../../interfaces/performance-analysis.interface';
import * as PerformanceAnalysisActions from './performance-analysis.actions';

export const performanceAnalysisFeatureKey = 'performanceAnalysis';

export interface State {
  filters: PerformanceAnalysisFilters | null;
  data: PerformanceAnalysisData | null;
  jobFilter: PerformanceAnalysisJobFilter;
  jobId: number | null;
  period: PerformanceAnalysisPeriod;
  customStartDate: string | null;
  customEndDate: string | null;
  loading: boolean;
  filtersLoading: boolean;
  exportingCsv: boolean;
  exportingPdf: boolean;
  error: unknown;
}

export const initialState: State = {
  filters: null,
  data: null,
  jobFilter: PerformanceAnalysisJobFilter.ALL_OPEN_JOBS,
  jobId: null,
  period: PerformanceAnalysisPeriod.LAST_30_DAYS,
  customStartDate: null,
  customEndDate: null,
  loading: false,
  filtersLoading: false,
  exportingCsv: false,
  exportingPdf: false,
  error: null,
};

export const performanceAnalysisReducer = createReducer(
  initialState,
  on(PerformanceAnalysisActions.loadPerformanceAnalysisFilters, (state) => ({
    ...state,
    filtersLoading: true,
    error: null,
  })),
  on(PerformanceAnalysisActions.loadPerformanceAnalysisFiltersSuccess, (state, action) => ({
    ...state,
    filters: action.filters,
    filtersLoading: false,
  })),
  on(PerformanceAnalysisActions.loadPerformanceAnalysisFiltersFailure, (state, action) => ({
    ...state,
    filtersLoading: false,
    error: action.error,
  })),
  on(PerformanceAnalysisActions.loadPerformanceAnalysis, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(PerformanceAnalysisActions.loadPerformanceAnalysisSuccess, (state, action) => ({
    ...state,
    data: action.data,
    loading: false,
  })),
  on(PerformanceAnalysisActions.loadPerformanceAnalysisFailure, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
  })),
  on(PerformanceAnalysisActions.setPerformanceAnalysisJobFilter, (state, action) => ({
    ...state,
    jobFilter: action.jobFilter,
    jobId: action.jobId ?? null,
  })),
  on(PerformanceAnalysisActions.setPerformanceAnalysisPeriod, (state, action) => ({
    ...state,
    period: action.period,
    customStartDate:
      action.startDate !== undefined ? action.startDate : state.customStartDate,
    customEndDate: action.endDate !== undefined ? action.endDate : state.customEndDate,
  })),
  on(PerformanceAnalysisActions.exportPerformanceAnalysisCsv, (state) => ({
    ...state,
    exportingCsv: true,
  })),
  on(PerformanceAnalysisActions.exportPerformanceAnalysisCsvSuccess, (state) => ({
    ...state,
    exportingCsv: false,
  })),
  on(PerformanceAnalysisActions.exportPerformanceAnalysisCsvFailure, (state, action) => ({
    ...state,
    exportingCsv: false,
    error: action.error,
  })),
  on(PerformanceAnalysisActions.exportPerformanceAnalysisPdf, (state) => ({
    ...state,
    exportingPdf: true,
  })),
  on(PerformanceAnalysisActions.exportPerformanceAnalysisPdfSuccess, (state) => ({
    ...state,
    exportingPdf: false,
  })),
  on(PerformanceAnalysisActions.exportPerformanceAnalysisPdfFailure, (state, action) => ({
    ...state,
    exportingPdf: false,
    error: action.error,
  })),
  on(PerformanceAnalysisActions.clearPerformanceAnalysis, () => initialState)
);

export const selectPerformanceAnalysisState = createFeatureSelector<State>(
  performanceAnalysisFeatureKey
);

export const selectPerformanceAnalysisFilters = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.filters
);

export const selectPerformanceAnalysisData = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.data
);

export const selectPerformanceAnalysisLoading = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.loading
);

export const selectPerformanceAnalysisFiltersLoading = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.filtersLoading
);

export const selectPerformanceAnalysisJobFilter = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.jobFilter
);

export const selectPerformanceAnalysisJobId = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.jobId
);

export const selectPerformanceAnalysisPeriod = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.period
);

export const selectPerformanceAnalysisCustomStartDate = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.customStartDate
);

export const selectPerformanceAnalysisCustomEndDate = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.customEndDate
);

export const selectPerformanceAnalysisQuery = createSelector(
  selectPerformanceAnalysisJobFilter,
  selectPerformanceAnalysisJobId,
  selectPerformanceAnalysisPeriod,
  selectPerformanceAnalysisCustomStartDate,
  selectPerformanceAnalysisCustomEndDate,
  (jobFilter, jobId, period, customStartDate, customEndDate) => ({
    jobFilter: jobId ? undefined : jobFilter,
    jobId: jobId ?? undefined,
    period,
    startDate:
      period === PerformanceAnalysisPeriod.CUSTOM && customStartDate
        ? customStartDate
        : undefined,
    endDate:
      period === PerformanceAnalysisPeriod.CUSTOM && customEndDate ? customEndDate : undefined,
  })
);

export const selectPerformanceAnalysisExporting = createSelector(
  selectPerformanceAnalysisState,
  (state) => state.exportingCsv || state.exportingPdf
);
